import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CustomDataAdditionalDataModel,
  CustomDataSearchFieldModel,
  DisplayFieldConfiguration,
  FieldDefinitionModel,
  FieldType,
  FlowModel,
  ObjectDefinitionModel,
  ScopeType,
  SearchOperators,
  SeahorseModule,
  UiModule,
  UiModuleDefinition,
  UiModuleOverviewConfiguration,
  UiModuleOverviewDefaultFilterConfig,
  UiModuleOverviewDisplayStyle,
  TaskProgressDisplayType,
  TaskProgressDisplayFormat,
  TaskProgressIndicatorType,
} from '@seahorse/domain';
import { UiModuleConfigurationHelpers } from '@seahorse/common';
import {
  ShButtonComponent,
  ShFieldComponent,
  ShInputComponent,
  ShMultiselectComponent,
  ShNumberComponent,
  ShSelectComponent,
  ShTab,
  ShTabPanelDirective,
  ShTabPanelsComponent,
  ShTabsComponent,
  ShToggleComponent,
} from '@seahorse/components';
import { ShCellDirective, ShTableComponent } from '@seahorse/shell';
import type { ShColumn } from '@seahorse/shell';
import type { ActionMenuItem } from '@seahorse/ui';
import {
  ConfigurationOption,
  createSearchOptions,
  OverviewSummaryAction,
  createTaskProgressDisplayFormatOptions,
  createTaskProgressDisplayTypeOptions,
  createTaskProgressIndicatorTypeOptions,
  DEFAULT_PROGRESS_COLOR_MAP,
  isOverviewConfigurationValid,
} from './ui-module-overview-configuration-form.model';
import { ProxyServices } from '../../../../shared/services/proxy.service';
import { FieldConfigurationTemplate } from '../templates/field-configuration-template/field-configuration.template';
import { FieldOperatorInputComponent } from '../field-operator-input/field-operator-input.component';
import { RequiredDataEditorComponent } from '../required-data-editor/required-data-editor.component';
import {
  ConfigValueCompat,
  DEFAULT_FILTER_FIELD_RULES,
  DISPLAY_FIELD_CONFIGURATION_RULES,
  OVERVIEW_CONFIGURATION_RULES,
  OVERVIEW_HEADER_RULES,
  OVERVIEW_SUMMARY_RULES,
} from '../config-value-compat';
import { TaskProgressColorMapEditorComponent } from './task-progress-color-map-editor/task-progress-color-map-editor.component';

/**
 * The overview configuration of a UI module (or of a Seahorse module): page
 * size, sorting, the displayed columns and, per tab, the optional header,
 * grouping, summary, required data, task progress and default filter. It
 * edits `configuration` in place; the hosts (the configuration dialog, the
 * add wizard) save it, after calling `prepareForSave()`.
 *
 * `form` is the template-driven form of the two required fields, so a host
 * can ask `form.form.valid` (or `isValid`).
 */
@Component({
  selector: 'su-ui-module-overview-configuration-form',
  templateUrl: './ui-module-overview-configuration-form.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ShButtonComponent,
    ShFieldComponent,
    ShInputComponent,
    ShMultiselectComponent,
    ShNumberComponent,
    ShSelectComponent,
    ShTabsComponent,
    ShTabPanelsComponent,
    ShTabPanelDirective,
    ShToggleComponent,
    ShTableComponent,
    ShCellDirective,
    FieldConfigurationTemplate,
    FieldOperatorInputComponent,
    RequiredDataEditorComponent,
    TaskProgressColorMapEditorComponent,
  ],
})
export class UiModuleOverviewConfigurationFormComponent implements OnInit {
  @Input() configuration: UiModuleOverviewConfiguration;
  @Input() submitted: boolean;
  @Input() module: UiModule | UiModuleDefinition;
  @Input() seahorseModule: SeahorseModule;
  @Input() invoiceFlows: FlowModel[] = [];

  @ViewChild('form') form: NgForm;

  // The original raw values of the stored configuration: the controls get
  // typed values, the save writes back what the old form would (see
  // config-value-compat).
  private readonly _configurationCompat = new ConfigValueCompat(
    OVERVIEW_CONFIGURATION_RULES
  );
  private readonly _headerCompat = new ConfigValueCompat(OVERVIEW_HEADER_RULES);
  private readonly _summaryCompat = new ConfigValueCompat(
    OVERVIEW_SUMMARY_RULES
  );
  private readonly _filterFieldCompat = new ConfigValueCompat(
    DEFAULT_FILTER_FIELD_RULES
  );
  private readonly _fieldConfigurationCompat = new ConfigValueCompat(
    DISPLAY_FIELD_CONFIGURATION_RULES
  );

  private readonly _translate = inject(TranslateService);
  private readonly _proxyService = inject(ProxyServices);

  newAction: OverviewSummaryAction = {
    displayName: '',
    icon: '',
    fieldCode: '',
  };

  // Stable fields, not getters: they feed PrimeNG inputs.
  readonly sortOrderOptions = [
    {
      label: this._translate.instant('shared.terms.ascending'),
      value: true,
    },
    {
      label: this._translate.instant('shared.terms.descending'),
      value: false,
    },
  ];
  readonly taskProgressDisplayTypeOptions =
    createTaskProgressDisplayTypeOptions();
  readonly taskProgressDisplayFormatOptions =
    createTaskProgressDisplayFormatOptions();
  readonly taskProgressIndicatorTypeOptions =
    createTaskProgressIndicatorTypeOptions();
  readonly searchOptions = createSearchOptions();

  readonly sections: ShTab[] = [
    { key: 'header', label: 'uiModules.headerConfiguration' },
    { key: 'groups', label: 'uiModules.overview.groupsConfiguration' },
    { key: 'summary', label: 'uiModules.overview.summaryConfiguration' },
    { key: 'requiredData', label: 'uiModules.requiredData' },
    { key: 'taskProgress', label: 'uiModules.showTasksProgress' },
    { key: 'defaultFilter', label: 'uiModules.defaultFilter' },
  ];
  activeSection = 'header';

  hasHeaderConfiguration = false;
  hasGroupsConfiguration = false;
  hasSummaryConfiguration = false;
  hasDefaultFilter = false;
  hasRequiredDataConfiguration = false;
  hasTasksProgressConfiguration = false;

  defaultFilterFields: CustomDataSearchFieldModel[] = [];
  defaultFilterRemainingFields: FieldDefinitionModel[] = [];

  simpleFieldDefinitions: FieldDefinitionModel[] = [];

  displayFields: FieldDefinitionModel[] = [];
  displayFieldsOptions: ConfigurationOption[] = [];
  /** The displayed fields that have no field configuration yet. */
  configurableFieldOptions: ConfigurationOption[] = [];
  displayFieldsControl = inject(FormBuilder).control<string[]>([]);
  includeInSearchControl = inject(FormBuilder).control<number>(null);

  invoiceFlowsControl = inject(FormBuilder).control<string[]>([]);
  invoiceFlowOptions: ConfigurationOption[] = [];
  selectedInvoiceFlowOptions: ConfigurationOption[] = [];
  companyFields: { label: string; value: string }[] = [];

  objectDefinition?: ObjectDefinitionModel;

  readonly actionColumns: ShColumn<OverviewSummaryAction>[] = [
    { key: 'displayName', header: 'uiModules.actionDisplayName' },
    { key: 'icon', header: 'uiModules.actionIcon' },
    { key: 'fieldCode', header: 'uiModules.fieldCode' },
  ];
  readonly actionRowActions: ActionMenuItem<OverviewSummaryAction>[] = [
    {
      label: 'shared.terms.remove',
      icon: 'fa-regular fa-trash',
      danger: true,
      command: (action) => this.removeAction(action),
    },
  ];

  readonly filterColumns: ShColumn<CustomDataSearchFieldModel>[] = [
    {
      key: 'fieldName',
      header: 'reports.customdata.fieldName',
      value: (field) => this.fieldLabel(field),
    },
    {
      key: 'criteria',
      header: 'reports.customdata.filterCriteria',
      type: 'custom',
    },
  ];
  readonly filterRowActions: ActionMenuItem<CustomDataSearchFieldModel>[] = [
    {
      label: 'shared.terms.remove',
      icon: 'fa-regular fa-trash',
      danger: true,
      command: (field) => this.deleteFilter(field),
    },
  ];

  /** Both required fields are filled. */
  get isValid(): boolean {
    return isOverviewConfigurationValid(this.configuration);
  }

  ngOnInit() {
    this.loadStoredValues();

    if (this.seahorseModule) {
      this.objectDefinition = this._proxyService.objectDefinitions.find(
        (x) => x.mappingKey === this.seahorseModule.mappingKey
      );
    } else {
      this.objectDefinition = this._proxyService.objectDefinitions.find(
        (x) => x.id === this.module.customObjectDefinitionId
      );
    }

    this.displayFields = ObjectDefinitionModel.getAllFieldDefinitions(
      this.objectDefinition
    );

    this.displayFieldsOptions = this.displayFields.map((field) => {
      return {
        label: field.name,
        value: field.systemCode,
      };
    });

    this.setUpDisplayColumnCodes(this.configuration);
    this.setUpInvoiceFlows();
    this.setUpCompanyFields();

    this.simpleFieldDefinitions = ObjectDefinitionModel.getAllFieldDefinitions(
      this.objectDefinition,
      true
    ).filter(
      (x) =>
        x.fieldType !== FieldType.LinkedObject &&
        x.fieldType !== FieldType.LinkedObjects &&
        x.fieldType !== FieldType.AutoNumbering
    );

    if (this.configuration.headerConfig) {
      this.hasHeaderConfiguration = true;
    }

    if (this.configuration.groupsConfig) {
      this.hasGroupsConfiguration = true;
    }

    if (this.configuration.summaryConfig) {
      this.hasSummaryConfiguration = true;
    }

    if (this.configuration && this.configuration.defaultFilter) {
      this.hasDefaultFilter = true;
      this.defaultFilterFields = this.configuration.defaultFilter.fields;
    }

    this.updateDefaultFilterRemainingFields();

    if (
      this.configuration &&
      this.configuration.requiredData &&
      this.configuration.requiredData.length > 0
    ) {
      this.hasRequiredDataConfiguration = true;
    }

    if (this.configuration?.taskProgressConfig) {
      this.hasTasksProgressConfiguration = true;
    }

    if (this.configuration?.defaultFilter?.includeInSearch !== undefined) {
      this.includeInSearchControl.patchValue(
        this.configuration?.defaultFilter?.includeInSearch
      );
    }

    this.includeInSearchControl.valueChanges.subscribe((value) => {
      if (!this.configuration.defaultFilter) {
        this.configuration.defaultFilter =
          {} as UiModuleOverviewDefaultFilterConfig;
      }
      this.configuration.defaultFilter.includeInSearch = value;
    });
  }

  /** The stored values in the types of the controls (the raw ones are remembered). */
  private loadStoredValues() {
    const configuration = this.configuration;
    this._configurationCompat.load(configuration);

    if (configuration.headerConfig) {
      this._headerCompat.load(configuration.headerConfig);
    }
    if (configuration.summaryConfig) {
      this._summaryCompat.load(configuration.summaryConfig);
    }
    configuration.defaultFilter?.fields?.forEach((field) =>
      this._filterFieldCompat.load(field)
    );
    configuration.displayFieldsConfiguration?.forEach((field) =>
      this._fieldConfigurationCompat.load(field)
    );
  }

  /**
   * Brings the configuration in the shape the API takes; hosts call it just
   * before saving: the summary fields are re-split with their escape
   * characters, and the default filter is rebuilt from the filter rows that
   * are filled in (none left: no default filter).
   */
  prepareForSave() {
    // What the user did not change goes back as stored; what was changed in
    // the type the old form saved (pageSize and the sort of a field as
    // strings, the search operators as numeric strings).
    this._configurationCompat.restoreInPlace(this.configuration);
    if (this.configuration.headerConfig) {
      this._headerCompat.restoreInPlace(this.configuration.headerConfig);
    }
    if (this.configuration.summaryConfig) {
      this._summaryCompat.restoreInPlace(this.configuration.summaryConfig);
    }
    if (this.configuration.displayFieldsConfiguration) {
      this.configuration.displayFieldsConfiguration =
        this.configuration.displayFieldsConfiguration.map((field) =>
          this._fieldConfigurationCompat.forSave(field)
        );
    }

    const summary = this.configuration.summaryConfig;
    if (summary && summary.fields) {
      let fieldsString = summary.fields.toString();
      fieldsString =
        UiModuleConfigurationHelpers.insertEscapeCharacters(fieldsString);

      const fieldsArray = fieldsString.split(',').map((field) => field.trim());
      summary.fields =
        UiModuleConfigurationHelpers.replaceEscapeCharacters(fieldsArray);
    }

    if (this.defaultFilterFields && this.defaultFilterFields.length > 0) {
      this.configuration.defaultFilter = {
        fields: this.defaultFilterFields
          .filter(
            (x) =>
              // == in stead of === is on purpose
              x.searchOperator == SearchOperators.IsEmpty ||
              x.searchOperator == SearchOperators.IsNotEmpty ||
              (x.searchValue &&
                x.searchOperator !== null &&
                x.searchOperator !== undefined)
          )
          .map((x) => this._filterFieldCompat.forSave(x)),
        scope: ScopeType.Custom,
        objectDefinitionSystemCode: this.objectDefinition.systemCode,
        includeInSearch: this.includeInSearchControl.value,
      };
    } else {
      this.configuration.defaultFilter = null;
    }
  }

  private setUpDisplayColumnCodes(
    configuration: UiModuleOverviewConfiguration
  ) {
    if (configuration?.displayColumnsCodes) {
      const displayColumnsCodes =
        this.configuration.displayColumnsCodes.split(',');
      this.displayFieldsControl.patchValue(displayColumnsCodes);
    }

    this.displayFieldsControl.valueChanges.subscribe((value) => {
      this.configuration.displayColumnsCodes = value.join(',');
    });

    if (configuration.displayFieldsConfiguration) {
      configuration.displayFieldsConfiguration.forEach((field) => {
        field.name = this.displayFieldsOptions.find(
          (x) => x.value === field.field
        )?.label;
      });
    }

    this.updateConfigurableFieldOptions();
  }

  private updateConfigurableFieldOptions() {
    const configured = (
      this.configuration.displayFieldsConfiguration ?? []
    ).map((f) => f.field);
    this.configurableFieldOptions = this.displayFieldsOptions.filter(
      (x) => !configured.includes(<string>x.value)
    );
  }

  private setUpInvoiceFlows() {
    this.invoiceFlowOptions = this.invoiceFlows.map((flow) => ({
      label: flow.name,
      value: flow.systemCode,
    }));

    if (this.configuration?.headerConfig) {
      const flowsSystemCodes =
        this.configuration.headerConfig.invoiceFlows || [];
      this.invoiceFlowsControl.patchValue(flowsSystemCodes);
      this.updateSelectedInvoiceFlowOptions();
    }

    this.invoiceFlowsControl.valueChanges.subscribe((value) => {
      if (this.configuration.headerConfig) {
        this.configuration.headerConfig.invoiceFlows = value;
        if (!value.includes(this.configuration.headerConfig.selectedFlow)) {
          this.configuration.headerConfig.selectedFlow = null;
        }
        this.updateSelectedInvoiceFlowOptions();
      }
    });
  }

  private updateSelectedInvoiceFlowOptions() {
    if (this.configuration?.headerConfig?.invoiceFlows) {
      this.selectedInvoiceFlowOptions = this.invoiceFlows
        .filter((flow) =>
          this.configuration.headerConfig.invoiceFlows.includes(flow.systemCode)
        )
        .map((flow) => ({
          label: flow.name,
          value: flow.systemCode,
        }));
    } else {
      this.selectedInvoiceFlowOptions = [];
    }
  }

  private setUpCompanyFields() {
    this.companyFields = this.displayFields
      .filter((field) => {
        if (field.fieldType !== FieldType.LinkedObject) {
          return false;
        }
        try {
          const additionalData: CustomDataAdditionalDataModel = JSON.parse(
            field.additionalData
          );
          return additionalData.mappingKey === '$companies_company_id';
        } catch (e) {
          return false;
        }
      })
      .map((field) => ({ label: field.name, value: field.systemCode }));
  }

  /** Adds an (empty) display configuration for a displayed field. */
  addFieldConfiguration(systemCode: unknown) {
    if (!systemCode) {
      return;
    }

    const newConfig: DisplayFieldConfiguration = {
      field: systemCode as string,
      name: this.displayFieldsOptions.find((x) => x.value === systemCode)
        ?.label,
    };

    this.configuration.displayFieldsConfiguration = [
      ...(this.configuration.displayFieldsConfiguration ?? []),
      newConfig,
    ];
    this.updateConfigurableFieldOptions();
  }

  setFieldConfigurations(configurations: DisplayFieldConfiguration[]) {
    this.configuration.displayFieldsConfiguration = configurations;
    this.updateConfigurableFieldOptions();
  }

  toggleHeaderConfiguration() {
    if (this.hasHeaderConfiguration) {
      this.configuration.headerConfig = {
        displayStyle: UiModuleOverviewDisplayStyle.List,
        invoiceFlows: [],
        selectedFlow: null,
        debtorSelector: null,
        showSimpleSearch: false,
        showAdvancedSearch: false,
        showExportButton: false,
        showAddButton: false,
        showBatchUpdateButton: false,
      };
    } else {
      this.configuration.headerConfig = null;
    }
  }

  toggleGroupsConfiguration() {
    if (this.hasGroupsConfiguration) {
      this.configuration.groupsConfig = {
        field: null,
        sort: null,
      };
    } else {
      this.configuration.groupsConfig = null;
    }
  }

  toggleSummaryConfiguration() {
    if (this.hasSummaryConfiguration) {
      this.configuration.summaryConfig = {
        actions: [],
        fields: null,
        allowEdit: false,
      };
    } else {
      this.configuration.summaryConfig = null;
    }
  }

  toggleTasksProgressConfiguration() {
    if (this.hasTasksProgressConfiguration) {
      this.configuration.taskProgressConfig = {
        displayType: TaskProgressDisplayType.AllTasks,
        displayFormat: TaskProgressDisplayFormat.Percentage,
        colorMapping: [...DEFAULT_PROGRESS_COLOR_MAP],
        indicatorType: TaskProgressIndicatorType.PlainText,
      };
    } else {
      this.configuration.taskProgressConfig = null;
    }
  }

  toggleDefaultFilter() {
    if (this.hasDefaultFilter) {
      this.configuration.defaultFilter = {
        scope: ScopeType.Custom,
        objectDefinitionSystemCode: null,
        includeInSearch: null,
        fields: [],
      };
      this.defaultFilterFields = [];
      this.defaultFilterRemainingFields = this.simpleFieldDefinitions;
    } else {
      this.configuration.defaultFilter = null;
      this.defaultFilterFields = [];
      this.defaultFilterRemainingFields = [];
    }
  }

  setGroupByField(systemCode: unknown) {
    this.configuration.groupsConfig.field = (systemCode as string) ?? null;
  }

  selectDefaultFilter(systemCode: unknown) {
    if (!systemCode) {
      return;
    }

    const field = new CustomDataSearchFieldModel();
    field.fieldName = systemCode as string;
    this.setDefaultFilterFields([...this.defaultFilterFields, field]);
  }

  findFieldType(field: CustomDataSearchFieldModel): FieldType {
    return this.simpleFieldDefinitions.find(
      (x) => x.systemCode === field.fieldName
    )?.fieldType;
  }

  fieldLabel(field: CustomDataSearchFieldModel): string {
    return (
      this.simpleFieldDefinitions.find((x) => x.systemCode === field.fieldName)
        ?.name ?? field.fieldName
    );
  }

  deleteFilter(field: CustomDataSearchFieldModel) {
    this.setDefaultFilterFields(
      this.defaultFilterFields.filter((x) => x !== field)
    );
  }

  /**
   * Replaces the list of filter rows (sh-table refreshes on a new array) and
   * keeps the configuration's own list the same one, as it always was.
   */
  private setDefaultFilterFields(fields: CustomDataSearchFieldModel[]) {
    this.defaultFilterFields = fields;
    if (this.configuration.defaultFilter) {
      this.configuration.defaultFilter.fields = fields;
    }
    this.updateDefaultFilterRemainingFields();
  }

  private updateDefaultFilterRemainingFields() {
    const used = this.defaultFilterFields.map((f) => f.fieldName);
    this.defaultFilterRemainingFields = this.simpleFieldDefinitions.filter(
      (x) => !used.includes(x.systemCode)
    );
  }

  addAction() {
    if (
      this.newAction.displayName &&
      this.newAction.icon &&
      this.newAction.fieldCode
    ) {
      this.configuration.summaryConfig.actions = [
        ...(this.configuration.summaryConfig.actions ?? []),
        { ...this.newAction },
      ];

      this.newAction = {
        displayName: '',
        icon: '',
        fieldCode: '',
      };
    }
  }

  removeAction(action: OverviewSummaryAction) {
    this.configuration.summaryConfig.actions =
      this.configuration.summaryConfig.actions.filter((x) => x !== action);
  }

  toggleRequiredDataConfiguration() {
    if (this.hasRequiredDataConfiguration) {
      if (!this.configuration.requiredData) {
        this.configuration.requiredData = [];
      }
    } else {
      this.configuration.requiredData = null;
    }
  }
}
